import { type CSSProperties, forwardRef, type ReactNode } from "react";
import { cn } from "@/shared/cn";
import { PanelToggle } from "@/shared/ui/primitives/PanelToggle";
import { FrameOpener } from "@/shared/ui/layout/FrameOpener";
import { PanelLeftCloseIcon, PanelLeftOpenIcon } from "@/shared/ui/layout/shell-icons";

export interface ConversationColumnProps {
  collapsed: boolean;
  mode: "home" | "wide" | "beside";
  onToggle: () => void;
  labels: { open: string; collapse: string; region: string };
  head: ReactNode;
  showHead: boolean;
  style?: CSSProperties;
  children: ReactNode;
}

export const ConversationColumn = forwardRef<HTMLElement, ConversationColumnProps>(
  function ConversationColumn(
    { collapsed, mode, onToggle, labels, head, showHead, style, children },
    ref,
  ) {
    return (
      <section
        ref={ref}
        aria-label={labels.region}
        data-frame="conv"
        data-mode={mode}
        data-collapsed={collapsed || undefined}
        style={collapsed ? undefined : style}
        className={cn(
          "relative flex min-h-0 min-w-0 flex-col border-r border-(--line) bg-(--z-conv)",
          "transition-[flex-basis,width] duration-[280ms] ease-(--ease-frame)",
          collapsed ? "w-(--rail-w) shrink-0" : mode === "beside" ? "shrink-0" : "flex-1",
        )}
      >
        {collapsed ? (
          <FrameOpener label={labels.open} onClick={onToggle}>
            <PanelLeftOpenIcon />
          </FrameOpener>
        ) : (
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
            {showHead && (
              <div className="flex h-(--head-h) shrink-0 items-center justify-between pr-[16px] pl-[20px]">
                <div className="flex flex-col leading-[1.25]">{head}</div>
                <PanelToggle label={labels.collapse} onClick={onToggle}>
                  <PanelLeftCloseIcon />
                </PanelToggle>
              </div>
            )}
            {children}
          </div>
        )}
      </section>
    );
  },
);
